import { createElement, Users, User, ChevronLeft, ChevronRight, Printer, X, ZoomIn, ZoomOut, LoaderCircle, AlertCircle, CheckCircle, Loader, Clock, type IconNode } from "lucide"

// Matches design guide fs-7 (14px), fs-6 (16px), fs-5 (20px), fs-4 (24px)
type IconSize = 14 | 16 | 20 | 24

interface CacheKey {
  name: string
  icon: IconNode
  size: IconSize
  className?: string
}

/**
 * Caches pre-rendered Lucide icon SVGs as HTML strings.
 * Icons are generated once and reused to avoid repeated DOM creation.
 */
class LucideIconCacheClass {
  private cache = new Map<string, string>()
  private iconRegistry = new Map<IconNode, string>()

  constructor() {
    // Register known icons with their names for cache key generation
    this.registerIcon(Users, "users")
    this.registerIcon(User, "user")
    this.registerIcon(ChevronLeft, "chevron-left")
    this.registerIcon(ChevronRight, "chevron-right")
    this.registerIcon(Printer, "printer")
    this.registerIcon(X, "x")
    this.registerIcon(ZoomIn, "zoom-in")
    this.registerIcon(ZoomOut, "zoom-out")
    this.registerIcon(LoaderCircle, "loader-circle")
    this.registerIcon(AlertCircle, "alert-circle")
    this.registerIcon(CheckCircle, "check-circle")
    this.registerIcon(Loader, "loader")
    this.registerIcon(Clock, "clock")
  }

  private registerIcon(icon: IconNode, name: string): void {
    this.iconRegistry.set(icon, name)
  }

  private getIconName(icon: IconNode): string {
    return this.iconRegistry.get(icon) || "unknown"
  }

  private getCacheKey(icon: IconNode, size: IconSize, className?: string): string {
    const iconName = this.getIconName(icon)
    return `${iconName}-${size}-${className || ""}`
  }

  private generateIcon(icon: IconNode, size: IconSize, className?: string): string {
    const svg = createElement(icon, {
      width: size,
      height: size,
      class: className
    })
    return svg.outerHTML
  }

  /**
   * Gets an icon SVG string, generating and caching it if needed.
   */
  get(icon: IconNode, size: IconSize, className?: string): string {
    const key = this.getCacheKey(icon, size, className)

    if (!this.cache.has(key)) {
      this.cache.set(key, this.generateIcon(icon, size, className))
    }

    return this.cache.get(key)!
  }

  /**
   * Pre-warms the cache with commonly used icons.
   * Call this at module load time to ensure icons are ready.
   */
  preload(icons: CacheKey[]): void {
    icons.forEach(({ icon, size, className }) => {
      this.get(icon, size, className)
    })
  }
}

// Export singleton instance
export const LucideIconCache = new LucideIconCacheClass()

// Re-export commonly used icons for convenience
export { Users, User, ChevronLeft, ChevronRight, Printer, X, ZoomIn, ZoomOut, LoaderCircle, AlertCircle, CheckCircle, Loader, Clock }
export type { IconNode }
